import * as THREE from 'three';

/**
 * Quadruped gait generator. Pure math, no meshes: given the dog's speed it
 * produces a foot position per leg (fore/aft offset + lift) and body bob/pitch.
 *
 * Feet are modelled in "stride space": during stance the foot travels backwards
 * under the body at exactly ground speed (so it plants — no skating), during
 * swing it arcs forward and up. Walk, trot and gallop differ in footfall
 * timing, duty factor (stance fraction), stride length and lift; the generator
 * blends between them by speed so there is never a hard gait switch.
 *
 * Leg order everywhere: FL, FR, BL, BR.
 */

export interface FootPose {
  /** fore/aft offset from the leg's neutral position, metres, +forward */
  z: number;
  /** height above ground, metres */
  lift: number;
}

interface GaitDef {
  /** phase offset per leg, in cycles. Stance starts at leg phase 0. */
  offsets: [number, number, number, number];
  /** stance fraction of the cycle */
  duty: number;
  /** metres travelled per cycle */
  stride: number;
  /** swing foot height (m) */
  lift: number;
  /** vertical body bob amplitude (m) */
  bob: number;
  /** body pitch amplitude (rad), nose-up positive */
  pitch: number;
}

// Walk: lateral sequence LH → LF → RH → RF, quarter cycle apart.
const WALK: GaitDef = { offsets: [0.25, 0.75, 0.0, 0.5], duty: 0.68, stride: 0.55, lift: 0.035, bob: 0.008, pitch: 0.01 };
// Trot: diagonal pairs (FL+BR, FR+BL) half a cycle apart. Offsets chosen so the
// fronts keep their walk timing and only the hinds shift when blending.
const TROT: GaitDef = { offsets: [0.25, 0.75, 0.75, 0.25], duty: 0.5, stride: 0.9, lift: 0.06, bob: 0.03, pitch: 0.02 };
// Transverse gallop: hinds land close together, then fronts, with one
// gathered suspension phase where nothing touches the ground.
const GALLOP: GaitDef = { offsets: [0.25, 0.35, 0.75, 0.85], duty: 0.35, stride: 1.7, lift: 0.11, bob: 0.06, pitch: 0.09 };

const TWO_PI = Math.PI * 2;

function smoothstep(a: number, b: number, x: number): number {
  const t = THREE.MathUtils.clamp((x - a) / (b - a), 0, 1);
  return t * t * (3 - 2 * t);
}

/** Weighted circular mean of three phase offsets (in cycles). */
function blendPhase(a: number, b: number, c: number, wa: number, wb: number, wc: number): number {
  const x = wa * Math.cos(a * TWO_PI) + wb * Math.cos(b * TWO_PI) + wc * Math.cos(c * TWO_PI);
  const y = wa * Math.sin(a * TWO_PI) + wb * Math.sin(b * TWO_PI) + wc * Math.sin(c * TWO_PI);
  return Math.atan2(y, x) / TWO_PI;
}

export class Gait {
  /** global stride phase in cycles (fractional part matters) */
  phase = 0;
  readonly feet: FootPose[] = [
    { z: 0, lift: 0 },
    { z: 0, lift: 0 },
    { z: 0, lift: 0 },
    { z: 0, lift: 0 },
  ];
  /** body vertical bob (m) */
  bob = 0;
  /** body pitch (rad), nose-up positive */
  pitch = 0;
  /** blend weights, exposed for debugging */
  wWalk = 1;
  wTrot = 0;
  wGallop = 0;

  // speed thresholds (m/s)
  static readonly TROT_IN = 0.55;
  static readonly TROT_FULL = 1.0;
  static readonly GALLOP_IN = 2.0;
  static readonly GALLOP_FULL = 2.8;

  update(dt: number, speed: number): void {
    // --- gait weights by speed
    const wt = smoothstep(Gait.TROT_IN, Gait.TROT_FULL, speed);
    const wg = smoothstep(Gait.GALLOP_IN, Gait.GALLOP_FULL, speed);
    const ww = (this.wWalk = 1 - wt);
    const wr = (this.wTrot = wt * (1 - wg));
    const wa = (this.wGallop = wt * wg);

    const duty = ww * WALK.duty + wr * TROT.duty + wa * GALLOP.duty;
    const stride = ww * WALK.stride + wr * TROT.stride + wa * GALLOP.stride;
    const lift = ww * WALK.lift + wr * TROT.lift + wa * GALLOP.lift;

    // phase advances with distance, so cadence follows speed automatically
    this.phase += (speed * dt) / stride;
    if (this.phase > 1e6) this.phase -= 1e6;

    // near standstill the feet ease to neutral instead of freezing mid-swing
    const g = smoothstep(0.05, 0.35, speed);
    const stanceTravel = stride * duty; // distance the foot covers while planted

    for (let i = 0; i < 4; i++) {
      const off = blendPhase(WALK.offsets[i], TROT.offsets[i], GALLOP.offsets[i], ww, wr, wa);
      let u = (this.phase + off) % 1;
      if (u < 0) u += 1;
      const f = this.feet[i];
      if (u < duty) {
        // stance: planted, sliding back under the body at ground speed
        f.z = (0.5 - u / duty) * stanceTravel * g;
        f.lift = 0;
      } else {
        // swing: smooth arc forward and up
        const p = (u - duty) / (1 - duty);
        f.z = (-0.5 + (1 - Math.cos(Math.PI * p)) * 0.5) * stanceTravel * g;
        f.lift = lift * Math.sin(Math.PI * p) * g;
      }
    }

    // --- body: two bounces per cycle in walk/trot (lowest at mid-stance),
    // one big bounce + pitch in gallop (highest during suspension).
    const ph = this.phase;
    const bob2 = (1 + Math.cos(TWO_PI * 2 * (ph - 0.25))) * 0.5;
    const bob1 = (1 + Math.cos(TWO_PI * (ph - 0.7))) * 0.5;
    this.bob = (ww * WALK.bob + wr * TROT.bob) * bob2 * g + wa * GALLOP.bob * bob1 * g;
    const pitch2 = Math.sin(TWO_PI * 2 * (ph - 0.25));
    const pitch1 = Math.cos(TWO_PI * (ph - 0.95));
    this.pitch = ((ww * WALK.pitch + wr * TROT.pitch) * pitch2 + wa * GALLOP.pitch * pitch1) * g;
  }
}
